/**
 * RowDetailPanel (#2302) — the Schlag leaf's calculation detail in a right-hand slide-in
 * panel. Thin wrapper: design-system ContextPanel (portal, Esc-close, overlay) around
 * RowDetail. Open ⇔ a row is selected (?cc=); closing clears the selection on the page.
 *
 * HOF-467 — RowDetail is keyed on the fact, so a newly opened row remounts it and its
 * drawer-local Bezugsgröße / Gliederung start from the page's ``?basis=`` again.
 *
 * 2026-09-07 — on a wide viewport the meta line and the switchers sit on the title row, right
 * of „Kultur – Schlag": the ContextPanel's `headerSlot` holds an element the organism portals
 * its header into. Under ``WIDE_TITLE_ROW`` (the drawer at its full 1240px, where the row has
 * the width) the organism keeps its header in place — a phone sheet's fixed header must not
 * swallow a third of the screen.
 */
import { useState } from 'react';
import { ContextPanel } from '@hofsync/design-system';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import type { MarginBasis } from './basis';
import RowDetail from './RowDetail';
import { makeRowLabeler } from './rowLabel';
import type { DeckungsbeitragRow } from './types';
import './RowDetailPanel.css';

/** The drawer is `min(1240px, 92vw)`; from 1344px on it is at its full width. */
export const WIDE_TITLE_ROW = '(min-width: 84rem)';

interface RowDetailPanelProps {
  /** The selected leaf row (?cc=) — null keeps the panel closed. */
  row: DeckungsbeitragRow | null;
  /** field_id → Schlag name, for the "Kultur – Schlag" title. */
  fieldNames: Record<string, string>;
  /** The active Bezugsgröße (#2304) — passed through for the geometry-assumption line. */
  basis?: MarginBasis;
  /** #2442 — the selected cascade line (?line=); null = none selected. */
  selectedLine?: string | null;
  /** #2442 — toggle a cascade line's source drill-down (null = deselect). */
  onSelectLine?: (lineKey: string | null) => void;
  /** #2442 — SPA navigation for a breakdown deep-link (page wires useNavigate). */
  onNavigate?: (to: string) => void;
  /** Close → the page clears ?cc= (selection and panel are ONE state). */
  onClose: () => void;
}

export default function RowDetailPanel({
  row,
  fieldNames,
  basis = 'ha',
  selectedLine = null,
  onSelectLine,
  onNavigate,
  onClose,
}: RowDetailPanelProps) {
  const labelOf = makeRowLabeler(fieldNames);
  // A state setter as the callback ref: the slot mounts with the panel (a portal), so an
  // effect over a ref object could not see it appear; the organism re-renders into it.
  const [titleRowSlot, setTitleRowSlot] = useState<HTMLDivElement | null>(null);
  const wide = useMediaQuery(WIDE_TITLE_ROW);
  return (
    <ContextPanel
      isOpen={row != null}
      onClose={onClose}
      title={row ? labelOf(row) : ''}
      width="wide"
      className="deckungsbeitrag-detail-panel"
      headerSlot={<div ref={setTitleRowSlot} className="deckungsbeitrag-detail-panel__title-row" />}
    >
      {row && (
        <RowDetail
          key={row.derived_fact_id}
          row={row}
          basis={basis}
          selectedLine={selectedLine}
          onSelectLine={onSelectLine}
          onNavigate={onNavigate}
          headerContainer={wide ? titleRowSlot : null}
        />
      )}
    </ContextPanel>
  );
}
